/**
 * Program Predecessor Conversion Utilities
 *
 * Shared utilities for converting program_predecessor values between RPN IDs and RPN aliases.
 * - ID to Alias: Used when reading data from backend to display to users
 * - Alias to ID: Used when submitting data back to backend
 */

import { FIRST_GENERATION_LABEL } from '../constants/appConstants';

/**
 * Converts a program_predecessor value from RPN ID to RPN alias (for display).
 * If the value is FIRST_GENERATION_LABEL or null, returns it unchanged.
 * If the value is a numeric RPN ID, converts it to the corresponding alias.
 * If the value is already an alias or not found, returns it unchanged.
 *
 * @param predecessorValue - The program_predecessor value to convert (may be RPN ID or alias)
 * @param rpnIdToAliasMap - Map of RPN ID to alias for lookup
 * @returns The converted value (alias) or original value if conversion not needed/possible
 *
 * @example
 * ```typescript
 * const map = new Map([[12345, "Echo Show Gen 3"]]);
 * const converted = convertProgramPredecessorToAlias("12345", map);
 * // Returns "Echo Show Gen 3"
 * ```
 */
export const convertProgramPredecessorToAlias = (
  predecessorValue: string | null,
  rpnIdToAliasMap: Map<number, string>
): string | null => {
  // If null, undefined, or FIRST_GENERATION_LABEL, leave as is
  if (!predecessorValue || predecessorValue === FIRST_GENERATION_LABEL) {
    return predecessorValue;
  }

  // Try to convert to number - if it's a valid RPN ID, look up the alias
  const predecessorId = Number(predecessorValue);

  if (!isNaN(predecessorId) && rpnIdToAliasMap.has(predecessorId)) {
    return rpnIdToAliasMap.get(predecessorId) || predecessorValue;
  }

  // If it's not a number or not found in the map, leave it as is (might already be an alias)
  return predecessorValue;
};

/**
 * Converts a program_predecessor value from RPN alias to RPN ID (for submission to backend).
 * If the value is FIRST_GENERATION_LABEL or null, returns it unchanged.
 * If the value is an RPN alias, converts it to the corresponding RPN ID (as string).
 * If the value is already an ID or not found, returns it unchanged.
 *
 * @param predecessorValue - The program_predecessor value to convert (may be alias or RPN ID)
 * @param rpnAliasToIdMap - Map of RPN alias to ID for lookup
 * @returns The converted value (RPN ID as string) or original value if conversion not needed/possible
 *
 * @example
 * ```typescript
 * const map = new Map([["Echo Show Gen 3", 12345]]);
 * const converted = convertProgramPredecessorToId("Echo Show Gen 3", map);
 * // Returns "12345"
 * ```
 */
export const convertProgramPredecessorToId = (
  predecessorValue: string | null,
  rpnAliasToIdMap: Map<string, number>
): string | null => {
  // If null, undefined, or FIRST_GENERATION_LABEL, leave as is
  if (!predecessorValue || predecessorValue === FIRST_GENERATION_LABEL) {
    return predecessorValue;
  }

  // Check if it's already a numeric ID - if so, leave as is
  const predecessorId = Number(predecessorValue);
  if (!isNaN(predecessorId)) {
    return predecessorValue;
  }

  // Try to find the ID by alias
  if (rpnAliasToIdMap.has(predecessorValue)) {
    const id = rpnAliasToIdMap.get(predecessorValue);
    return id !== undefined ? String(id) : predecessorValue;
  }

  // If not found in map, leave it as is
  return predecessorValue;
};

// Backward compatibility: Keep the original function name as alias to the new one
export const convertProgramPredecessorValue = convertProgramPredecessorToAlias;

/**
 * Describes how an FPN program_predecessor value should be displayed.
 * - `isLinkable: true` — render `displayText` as a hyperlink to `href`
 * - `isLinkable: false` — render `displayText` as plain text (no href)
 */
export interface PredecessorDisplay {
  isLinkable: boolean;
  href: string | null;
  displayText: string;
}

/**
 * Resolves an FPN program_predecessor value (stored as a numeric RPN ID) into the
 * information needed to render it — either as a hyperlink to the referenced RPN or as
 * plain text.
 *
 * On FPNs, program_predecessor is stored as the numeric RPN ID (unlike RPNs, where it is
 * stored as an alias). This helper is the single source of truth for both the read-only
 * and editable FPN Program Overview views so the two render identically.
 *
 * A predecessor is linkable only when all of the following hold:
 * - the value is non-empty and not FIRST_GENERATION_LABEL (a sentinel, not an RPN),
 * - the value parses to a numeric RPN ID,
 * - a productCode is available to build the RPN path.
 *
 * The display text prefers the resolved RPN alias, falling back to the raw value.
 *
 * @param predecessorValue - The stored program_predecessor value (numeric RPN ID as string)
 * @param productCode - The product code used to build the RPN path (from the minimal hierarchy)
 * @param rpnIdToAliasMap - Map of RPN ID to alias for display and link-eligibility
 * @param buildHref - Function that builds an RPN path from (productCode, rpnDisplayId)
 * @returns How to display the predecessor (linkable flag, href, display text)
 *
 * @example
 * ```typescript
 * const map = new Map([[11243, 'Echo Show Gen 3']]);
 * resolvePredecessorDisplay('11243', 'ABC123', map, buildRpnPath);
 * // { isLinkable: true, href: '/manage-programs/product-code/ABC123/rpn/R11243', displayText: 'Echo Show Gen 3' }
 * ```
 */
export const resolvePredecessorDisplay = (
  predecessorValue: string | null | undefined,
  productCode: string | null | undefined,
  rpnIdToAliasMap: Map<number, string>,
  buildHref: (productCode: string, rpnDisplayId: string) => string
): PredecessorDisplay => {
  // Empty value: nothing to display, no link.
  if (!predecessorValue) {
    return { isLinkable: false, href: null, displayText: '' };
  }

  // FIRST_GENERATION_LABEL is a sentinel meaning "no predecessor" — never a link.
  if (predecessorValue === FIRST_GENERATION_LABEL) {
    return { isLinkable: false, href: null, displayText: predecessorValue };
  }

  const predecessorId = Number(predecessorValue);
  const alias = !isNaN(predecessorId) ? rpnIdToAliasMap.get(predecessorId) : undefined;
  const displayText = alias || predecessorValue;

  // Linkable only when we can resolve a numeric RPN ID and have a product code to build the path.
  const isLinkable = !isNaN(predecessorId) && !!productCode;

  return {
    isLinkable,
    href: isLinkable ? buildHref(productCode as string, `R${predecessorId}`) : null,
    displayText,
  };
};
